// Pure helpers for the home dashboard and permit list: no React, no browser APIs.
// Relative imports only, so tests/unit/shell-home.test.ts can run this file without
// the '@/' path alias. Everything here arranges server answers for display; nothing
// here decides whether work may proceed.
import { CYCLE_STATES, PERMIT_STATES, ROLES } from '../../domain/catalogue'
import type { CommandName, Eligibility, Me, PermitSummary } from '../../domain/types'

export type Tone = 'neutral' | 'info' | 'ok' | 'warn' | 'bad'

export function humanise(code: string): string {
  const s = code.replace(/_/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : code
}

/** "1 permit", "3 permits". */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

export function permitStateInfo(state: string): { label: string; tone: Tone } {
  return PERMIT_STATES[state] ?? { label: humanise(state), tone: 'neutral' }
}

export function cycleStateInfo(state: string): { label: string; tone: Tone } {
  return CYCLE_STATES[state] ?? { label: humanise(state), tone: 'neutral' }
}

const ELIGIBILITY_SUMMARY: Record<Eligibility['status'], { label: string; tone: Tone }> = {
  recorded_conditions_satisfied: { label: 'Work may proceed - recorded conditions satisfied', tone: 'ok' },
  blocked: { label: 'Work must not proceed', tone: 'bad' },
  unresolved: { label: 'Work must not proceed - a rule or value is unresolved', tone: 'warn' },
}

/** The server's eligibility status in words. An unknown status is read as "must not proceed". */
export function eligibilityInfo(status: string): { label: string; tone: Tone; mayProceed: boolean } {
  const known = (ELIGIBILITY_SUMMARY as Record<string, { label: string; tone: Tone }>)[status]
  if (!known) return { label: 'Work must not proceed - status not recognised', tone: 'bad', mayProceed: false }
  return { ...known, mayProceed: status === 'recorded_conditions_satisfied' }
}

const ACTION_LABELS: Partial<Record<CommandName, string>> = {
  create_permit_draft: 'Start a draft',
  revise_permit_draft: 'Finish or revise the draft',
  submit_permit: 'Submit the permit',
  return_for_revision: 'Return for revision',
  recall_permit_application: 'Recall the application',
  record_meeting_review: 'Record the permit meeting review',
  record_pc_approval: 'Record PC approval',
  record_oac_agreement: 'Agree as affected custodian',
  record_worksite_examination: 'Record the worksite visit',
  record_supporting_document: 'Record a supporting document',
  verify_supporting_document: 'Verify a supporting document',
  withdraw_supporting_document: 'Withdraw a supporting document',
  authorise_permit: 'Authorise the permit',
  approve_permit_om: 'Approve the permit as Operations Manager',
  request_cycle: 'Request a daily cycle',
  preissue_cycle: 'Pre-issue the cycle',
  validate_cycle: 'Validate the cycle',
  record_gas_test: 'Record a gas test',
  record_toolbox_talk: 'Record the toolbox talk',
  sign_tbt_attendance: 'Sign the toolbox talk',
  accept_cycle: 'Accept the cycle',
  report_suspension: 'Report a stop',
  confirm_suspension: 'Confirm the stop',
  request_scope_change: 'Report a scope change',
  request_extension: 'Request an extension',
  approve_extension: 'Decide the extension request',
  refuse_extension: 'Decide the extension request',
  withdraw_extension: 'Withdraw the extension request',
  sign_ph_cancellation: 'Cancel the permit',
  close_out_permit: 'Close out the permit',
}

/** Pending actions arrive as command names; anything else is shown as sent. */
export function pendingActionLabel(action: string): string {
  return (ACTION_LABELS as Record<string, string>)[action] ?? humanise(action)
}

/**
 * Steps anyone eligible may take at any time when something happens (a stop, a scope
 * change, a withdrawn document). They stay on the permit page, but a to-do list must
 * not say "Waiting for you: Report a stop" as if a stop needed reporting.
 */
const REACTIVE_ACTIONS = new Set<string>([
  'report_suspension',
  'request_scope_change',
  'withdraw_supporting_document',
  // Asking for or withdrawing an extension (0020) is optional, never a to-do.
  'request_extension',
  'withdraw_extension',
])

/**
 * The pending steps that are real to-dos for this person. Approving and refusing an
 * extension are one decision, listed once.
 */
export function waitingActions(p: Pick<PermitSummary, 'my_pending_actions'>): string[] {
  const out = p.my_pending_actions.filter((a) => !REACTIVE_ACTIONS.has(a))
  return out.includes('approve_extension') ? out.filter((a) => a !== 'refuse_extension') : out
}

export function roleLabel(code: string): string {
  const r = (ROLES as Record<string, { label: string; short: string }>)[code]
  return r ? `${r.label} (${r.short})` : code
}

/** A badge while the Operations Manager approval is pending (0016); nothing otherwise. */
export function omPendingInfo(p: Pick<PermitSummary, 'om_approval' | 'lifecycle_state'>): { label: string; tone: Tone } | null {
  if (p.om_approval !== 'pending' || p.lifecycle_state !== 'authorised') return null
  return { label: 'OM approval pending', tone: 'warn' }
}

export function isMine(p: Pick<PermitSummary, 'my_roles' | 'my_pending_actions'>): boolean {
  return p.my_roles.length > 0 || waitingActions(p).length > 0
}

export type PermitFilter = { state: string; q: string; mine: boolean }

function byUpdatedDesc(a: PermitSummary, b: PermitSummary): number {
  return b.updated_at.localeCompare(a.updated_at) || a.permit_number.localeCompare(b.permit_number)
}

/** Client-side filter of the visibility-checked list the server returned. */
export function filterPermits(list: readonly PermitSummary[], f: PermitFilter): PermitSummary[] {
  const q = f.q.trim().toLowerCase()
  return list
    .filter((p) => !f.state || p.lifecycle_state === f.state)
    .filter((p) => !f.mine || isMine(p))
    .filter((p) => {
      if (!q) return true
      const hay = [p.permit_number, p.title, p.site_area.name, p.site_area.code, p.site.name].join(' ').toLowerCase()
      return q.split(/\s+/).every((word) => hay.includes(word))
    })
    .sort(byUpdatedDesc)
}

/** Permits where the server lists a step for this person now (reactive steps excluded). */
export function waitingForMe(list: readonly PermitSummary[]): PermitSummary[] {
  return list.filter((p) => waitingActions(p).length > 0).sort(byUpdatedDesc)
}

/** Permits this person is named on (any role). */
export function myPermits(list: readonly PermitSummary[]): PermitSummary[] {
  return list.filter((p) => p.my_roles.length > 0).sort(byUpdatedDesc)
}

/**
 * Eligible applicants hold PA or RS (CORE-026; BadrEOR has no Area Authority from 0016) and
 * the site's apply grant.
 */
const APPLICANT_ROLES = ['PA', 'RS']

/**
 * Whether to offer "New permit". Only a shortcut: the server checks the grant, the
 * credential, its scope and expiry when the draft is created.
 */
export function canStartPermit(me: Pick<Me, 'credentials' | 'sites'>): boolean {
  const hasRole = me.credentials.some((c) => APPLICANT_ROLES.includes(c.role_code))
  const hasGrant = me.sites.some((s) => s.capabilities.includes('apply_permits'))
  return hasRole && hasGrant
}

export function credentialStatusTone(status: string): Tone {
  const s = status.toLowerCase()
  if (['current', 'valid', 'active', 'verified'].includes(s)) return 'ok'
  if (['expired', 'revoked', 'suspended', 'withdrawn', 'invalid'].includes(s)) return 'bad'
  return 'warn'
}
